import { estClient } from './estClient';

/**
 * '-1' - all
 * '1' - prices
 * '3' - units
 * '4' - unit data
 * '6' - appeals
 * '7' - tasks
 * '8' - calls
 */
export type SheetType = '1' | '3' | '4' | '6' | '7' | '8' | '-1';

export interface SheetInfo {
  id: string;
  exportType: SheetType;
  projectId: string | null;

  exportFileId: string;
  filename: string;

  /**
   * @example '2023-07-30 22:31:38'
   */
  dateCreated: string;

  /**
   * @example '2023-07-30 22:31:38'
   */
  priceDate: string | null;

  createdBy: string;
  isDeleted: '0' | '1';
}

export interface SheetsList {
  success: true;
  data:
    | {
        type: '-1';
        spreadsheets: {
          prices: SheetInfo[];
          units: SheetInfo[];
          appeals: SheetInfo[];
        };
      }
    | {
        type: '1';
        spreadsheets: {
          prices: SheetInfo[];
        };
      }
    | {
        type: '3';
        spreadsheets: {
          units: SheetInfo[];
        };
      }
    | {
        /** Unit data export */
        type: '4';
        spreadsheets: {
          units: SheetInfo[];
        };
      }
    | {
        type: '6';
        spreadsheets: {
          appeals: SheetInfo[];
        };
      }
    | {
        type: '7';
        spreadsheets: {
          appeals: SheetInfo[];
        };
      }
    | {
        type: '8';
        spreadsheets: {
          appeals: SheetInfo[];
        };
      };
}

export interface SheetProjectInfo {
  /**
   * Project name
   */
  name: string;

  exports: {
    /**
     * Table id
     */
    id: string;

    /**
     * Google docs url
     */
    url: string;

    /**
     * Won't be available for units data export
     *
     * @example '2024-05-26'
     */
    prices_from?: string;

    /**
     * @example '2024-05-25 12:08:45'
     */
    created_at: string;
    created_by: {
      name: string;
      img_path: string;
    };
  }[];

  /**
   * @example ['2024-05-26']
   */
  taken_dates: string[];
}

interface GoogleAuthErrorResponse {
  notAuth: true;
  authUrl: string;
}

export class GoogleAuthError extends Error {
  name = this.constructor.name;
  url = '';

  constructor(data: GoogleAuthErrorResponse) {
    super('');

    this.message = 'Google auth error';
    this.url = data.authUrl;
  }

  getUrl() {
    return this.url;
  }
}

class SheetsApi {
  async getList({ type }: { type?: SheetType } = {}): Promise<SheetsList> {
    const resp = await estClient.get<SheetsList | GoogleAuthErrorResponse>(
      `/api/sheets${type ? `/${type}` : ''}`,
    );

    if (resp['notAuth']) {
      throw new GoogleAuthError(resp as GoogleAuthErrorResponse);
    }

    return resp as SheetsList;
  }

  async requestExport({ type }: { type: SheetType }): Promise<SheetsList> {
    const resp = await estClient.post<SheetsList | GoogleAuthErrorResponse>(
      `/api/sheets/${type}`,
    );

    if (resp['notAuth']) {
      throw new GoogleAuthError(resp as GoogleAuthErrorResponse);
    }

    return resp as SheetsList;
  }

  /**
   * Request current state of export by export type.
   * Find out whether there is already google sheet table created.
   */
  async getProjectInfo({
    projectId,
    type,
  }: {
    projectId: string;
    type: SheetType;
  }): Promise<SheetProjectInfo> {
    const resp = await estClient.get<
      SheetProjectInfo | GoogleAuthErrorResponse
    >(`/api/sheets/project-exports/${type}/${projectId}`);

    if (resp['notAuth']) {
      throw new GoogleAuthError(resp as GoogleAuthErrorResponse);
    }

    return resp as SheetProjectInfo;
  }

  async createTable(body: {
    projectId: string;
    type: SheetType;

    /**
     * Optional for unit data export
     */
    date?: string;
  }): Promise<SheetProjectInfo> {
    if (body.type !== this.SHEET_TYPE.UNITS_DATA && !body.date) {
      throw new Error(
        'Expected date to exist when creating new sheet export table',
      );
    }

    const resp = await estClient.post<
      SheetProjectInfo | GoogleAuthErrorResponse
    >(`/api/sheets/tables`, { body });

    if (resp['notAuth']) {
      throw new GoogleAuthError(resp as GoogleAuthErrorResponse);
    }

    return resp as SheetProjectInfo;
  }

  async importTable(tableId: string) {
    return await estClient.get<{
      error: 0 | 1;
      message: string;
      changes: boolean;
      unitsIgnored: string[];
    }>(`/api/sheets/tables/${tableId}/import`);
  }

  /**
   * For some reason unit export needs separate API for import
   */
  async importUnitsDataTable(tableId: string) {
    return await estClient.get<{
      error: 0 | 1;
      message: string;
      changes: boolean;
      unitsIgnored: string[];
    }>('/api/sheets/tables', {
      query: {
        table_id: tableId,
      },
    });
  }

  async deleteTable(tableId: string) {
    await estClient.delete<{ success: boolean }>(
      `/api/sheets/tables/${tableId}`,
    );
  }

  get SHEET_TYPE() {
    return {
      PRICES: '1',
      UNITS: '3',
      UNITS_DATA: '4',
      APPEALS: '6',
      TASKS: '7',
      CALLS: '8',
    } as const;
  }
}

export const sheetsApi = new SheetsApi();
